import React, { FC, RefObject, useCallback, useMemo, useState } from "react";
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  ScrollView as ScrollViewNative,
  TouchableOpacity,
} from "react-native";
import { Box, ButtonIcon, InputField, View, COLOR, INPUT_TYPES, Touchable } from "@lookiero/aurora";
import { useI18n, useI18nMessage } from "@lookiero/i18n-react";
import { Button, Icon, Layout, Modal, Text } from "@lookiero/sty-psp-ui";
import { Product } from "../../../../../domain/entities/product/Product";
import { ProductPreview } from "../../../../../domain/projections/ProductPreview";
import { VariantWithColorAndImage } from "../../../../../domain/projections/VariantWithColorAndImage";
import { useGetDistributionByFeatures } from "../../../behaviors/useGetDistributionByFeatures";
import { Label } from "../../../components/label/Label";
import { StackedBanner } from "../../../components/stackedBanner/StackedBanner";
import { useExtendedScreen } from "../../../hooks/useExtendedScreen";
import { useFormatCurrency } from "../../../hooks/useFormatCurrency";
import { DOMAIN, I18nMessages } from "../../../i18n/i18n";
import { OS } from "../../../shared/config";
import { financial } from "../../../shared/util/financial/financial";
import { ProductDetailFooter } from "../footer/productDetailFooter/ProductDetailFooter";
import { ProductDetailHeader } from "../header/productDetailHeader/ProductDetailHeader";
import { returnAndDeliveryFeatures } from "./ProductDetail.definition";
import { style as productDetailStyle } from "./ProductDetail.styles";
import { ExpandableColorPicker } from "./expandableColorPicker/ExpandableColorPicker";
import { Features } from "./features/Features";
import { GarmentCareIcons } from "./garmentCareIcons/GarmentCareIcons";
import { LookAndLikeProducts } from "./lookAndLikeProducts/LookAndLikeProducts";
import { OtherLookProducts } from "./otherLookProducts/OtherLookProducts";
import { ProductFeature } from "./productFeature/ProductFeature";
import { ProductImageGallery } from "./productImageGallery/ProductImageGallery";
import { ScrollableColorPicker } from "./scrollableColorPicker/ScrollableColorPicker";
import { SimilarProducts } from "./similarProducts/SimilarProducts";
import { SliderPreviewModal } from "./sliderPreviewModal/SliderPreviewModal";
import { SoldOutSizeModal } from "./soldOutSizeModal/SoldOutSizeModal";

const ACTIVE_OPACITY = 0.6;

interface ProductDetailProps {
  readonly layout: Layout;
  readonly benefitsMediaUrl: string;
  readonly hasNotification: boolean;
  readonly lookIdFromRoute: string;
  readonly product: Product;
  readonly scrollViewRef: RefObject<ScrollViewNative | null>;
  readonly variantIdFromRoute: string;
  readonly addProductToStockNotification: (variantId: string) => void;
  readonly onPressBasket: () => void;
  readonly goToWishlist: () => void;
  readonly onChangeColor: (color: VariantWithColorAndImage) => void;
  readonly onLayout: () => void;
  readonly onChangeProductSize: (variantId: string) => void;
  readonly onPressAddToBasket: () => void;
  readonly onPressWishlist: () => void;
  readonly onPressOtherLookProduct: () => void;
  readonly selectedSize: string;
  readonly scrollToBenefits: (coordinateY: number) => void;
  readonly showDeliveryAndReturn: boolean;
  readonly scrollToWatchDistributions: (coordinateY: number) => void;
  readonly onScroll: (coords: { X: number; Y: number }) => void;
  readonly onPressProduct: (product: ProductPreview) => void;
  readonly onBack: () => void;
}

const ProductDetail: FC<ProductDetailProps> = ({
  layout: Layout,
  benefitsMediaUrl,
  hasNotification,
  lookIdFromRoute,
  product,
  scrollViewRef,
  variantIdFromRoute,
  addProductToStockNotification,
  onPressBasket,
  goToWishlist,
  onChangeProductSize,
  onChangeColor,
  onLayout,
  onPressAddToBasket,
  onPressWishlist,
  onPressOtherLookProduct,
  selectedSize,
  scrollToBenefits,
  showDeliveryAndReturn,
  scrollToWatchDistributions,
  onScroll,
  onPressProduct,
  onBack,
}) => {
  const screen = useExtendedScreen();

  const style = useMemo(() => productDetailStyle(), []);

  const [footerHeight, setFooterHeight] = useState(0);
  const handleOnLayoutFooter = useCallback(
    (event: LayoutChangeEvent) => Platform.OS !== "web" && setFooterHeight(event.nativeEvent.layout.height),
    [],
  );

  const formatCurrency = useFormatCurrency();
  const { formatMessage } = useI18n({ domain: DOMAIN });
  const emptyOptionText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.EMPTY_OPTION,
  });
  const productRecomendedSizeText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_RECOMMENDED_SIZE,
  });
  const productNotAvailableItemText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_NOT_AVAILABLE_ITEM,
  });
  const labelOutOfStockText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.LABEL_OUT_OF_STOCK,
  });
  const productStockOutModalTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_STOCK_OUT_MODAL_TITLE,
  });
  const productLabelLastItemsQuantityText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_LABEL_LAST_ITEMS_QUANTITY,
    values: { quantity: product.lastUnitsStock },
  });
  const labelLastItemsText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.LABEL_LAST_ITEMS,
    values: { quantity: product.lastUnitsStock },
  });
  const productDescriptionTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_DESCRIPTION_TITLE,
  });
  const productSizeAdjustTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_SIZE_ADJUST_TITLE,
  });
  const productSizeModelText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_SIZE_MODEL,
  });
  const productCompositionCareTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_COMPOSITION_CARE_TITLE,
  });
  const prudctSizeSelectorText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_SIZE_SELECTOR,
  });
  const productStockOutCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_STOCK_OUT_CTA,
  });
  const productAddToCartCtaText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_ADD_TO_CART_CTA,
  });
  const benefitsCylSubtitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BENEFITS_CYL_SUBTITLE,
  });
  const benefitsCylTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.BENEFITS_CYL_TITLE,
  });
  const productWatchPpText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_WATCH_PP,
  });
  const productDeliveryTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_DELIVERY_TITLE,
  });
  const productDeliveryBodyText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_DELIVERY_BODY,
  });
  const productAvailabilityModalTitleText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_AVAILABILITY_MODAL_TITLE,
  });
  const productAvailabilityModalBodyText = useI18nMessage({
    domain: DOMAIN,
    id: I18nMessages.PRODUCT_AVAILABILITY_MODAL_BODY,
  });

  const [selectedIndexImage, setSelectedIndexImage] = useState<number>(-1);
  const [isOpenSoldOutSizeModal, setOpenSoldOutSizeModal] = useState<boolean>(false);
  const [distributionsCoordinateY, setDistributionsCoordinateY] = useState<number>(0);

  const [benefitsCoordinateY, setBenefitsCoordinateY] = useState<number>(0);
  const [showBenefits, setShowBenefits] = useState<boolean>(true);
  const hideBenefits = () => {
    setShowBenefits(false);
  };

  const distributions = useGetDistributionByFeatures(product.features);

  const isUniqueSize = product.size === "UNQ";

  const hasSelectedSize = Boolean(selectedSize) && emptyOptionText !== selectedSize;
  const wishlistIconColor = !hasSelectedSize
    ? style.colorTextMedium
    : product.wishlisted
      ? style.colorBgRose
      : style.colorText;
  const isOpenPreviewModal = selectedIndexImage !== -1;
  const isDesktopScreen = screen.XL;
  const isEnabledCYLModule = false;

  const [availabilityModalVisible, setAvailabilityModalVisible] = useState(false);

  const hideAvailabilityModal = useCallback(() => {
    setAvailabilityModalVisible(false);
  }, []);

  const showAvailabilityModal = useCallback(() => {
    setAvailabilityModalVisible(true);
  }, []);

  const onClickImage = useCallback((index: number) => {
    setSelectedIndexImage(index);
  }, []);

  const onClosePreviewModal = useCallback(() => {
    setSelectedIndexImage(-1);
  }, []);

  const onCloseSoldOutSizeModal = useCallback(() => {
    setOpenSoldOutSizeModal(false);
  }, []);

  const onPressSoldOutModalButton = useCallback(() => {
    setOpenSoldOutSizeModal(false);

    addProductToStockNotification(product.variantId);
  }, [addProductToStockNotification, product.variantId]);

  const onPressOpenSoldOutSizeModal = useCallback(() => {
    setOpenSoldOutSizeModal(true);
  }, []);

  const onPressProductImageGalleryButton = useCallback(() => {
    scrollToBenefits(benefitsCoordinateY);
  }, [benefitsCoordinateY, scrollToBenefits]);

  const onPressWatchDistributions = useCallback(() => {
    scrollToWatchDistributions(distributionsCoordinateY);
  }, [distributionsCoordinateY, scrollToWatchDistributions]);

  const onLayoutBeneftisBanner = useCallback(
    (event: LayoutChangeEvent) => setBenefitsCoordinateY(event.nativeEvent.layout.y),
    [],
  );

  const onLayoutDistributions = useCallback(
    (event: LayoutChangeEvent) => setDistributionsCoordinateY(event.nativeEvent.layout.y),
    [],
  );

  const handleOnScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      onScroll({ X: event.nativeEvent.contentOffset.x, Y: event.nativeEvent.contentOffset.y });
    },
    [onScroll],
  );

  const sizeOptions = useMemo(() => {
    return product.sizeVariants?.reduce(
      (arrayOfSizes, sizeVariant) => {
        arrayOfSizes.push({
          name: `${sizeVariant.size}${
            sizeVariant.available
              ? sizeVariant.isSizeRecommended
                ? ` (${productRecomendedSizeText})`
                : ""
              : ` (${productNotAvailableItemText})`
          }`,
          value: sizeVariant.variantId,
        });
        return arrayOfSizes;
      },
      [
        {
          name: emptyOptionText,
          value: emptyOptionText,
        },
      ],
    );
  }, [product.sizeVariants, emptyOptionText, productRecomendedSizeText, productNotAvailableItemText]);

  const showWatchDistributions = product.media && product.media.length > 2;

  return (
    <>
      <Layout
        scrollEnabled={false}
        style={{ header: style.header, scrollView: style.scrollView }}
        header={
          <ProductDetailHeader
            hasNotification={hasNotification}
            onBack={onBack}
            onPressBasket={onPressBasket}
            onPressWishlist={goToWishlist}
          />
        }
        onLayout={onLayout}
      >
        <ScrollView
          ref={scrollViewRef}
          contentContainerStyle={{ paddingBottom: footerHeight }}
          showsVerticalScrollIndicator={false}
          onScroll={handleOnScroll}
        >
          <View style={isDesktopScreen ? style.largeWrapper : style.smallWrapper}>
            {!isDesktopScreen && (
              <View style={style.productBrandPricingSmall}>
                <View style={style.productBrandSmall}>
                  <Text level={1} style={style.brand} testID="product-brand" variant="detail">
                    {product.brand}
                  </Text>

                  <Text level={1} testID="product-title" variant="detail">
                    {product.title}
                  </Text>
                </View>

                <View>
                  {!product.isProductWithDiscount ? (
                    <Text level={3} testID="product-price">
                      {formatCurrency({ amount: financial(product.price.amount) })}
                    </Text>
                  ) : (
                    <View testID="sales-pricing">
                      <View style={style.priceContent}>
                        <Text level={1} style={style.salesPricing} testID="product-price" variant="detail">
                          {formatCurrency({ amount: financial(product.price.amount) })}
                        </Text>

                        <Text level={1} testID="product-discounted-price" variant="detail">
                          {product.price.discountedPrice &&
                            formatCurrency({ amount: financial(product.price.discountedPrice?.amount) })}
                        </Text>
                      </View>

                      <Text
                        level={1}
                        style={style.discount}
                        testID="product-price-discount"
                        variant="detail"
                      >{`-${product.price.discountedPrice?.percentage}%`}</Text>
                    </View>
                  )}
                </View>

                <View style={style.labelsContainer}>
                  {!product.hasAvailableSizes ? (
                    <View style={style.labelWrapper} testID="no-stock-view">
                      <Label color={COLOR.BG_WARNING} style={style.noStockLabel}>
                        {labelOutOfStockText}
                      </Label>

                      <ButtonIcon name="info" onPress={showAvailabilityModal} />
                    </View>
                  ) : null}

                  {product.hasAvailableSizes && !product.isAvailable ? (
                    <View style={style.labelWrapper} testID="no-available-label">
                      <Label color={COLOR.BG_WARNING}>{productStockOutModalTitleText}</Label>
                    </View>
                  ) : null}

                  {product.isAvailable && product.lastUnits ? (
                    <View style={style.labelWrapper} testID="last-units">
                      <Label color={COLOR.BG_WARNING_LIGHT}>
                        {product.lastUnitsStock ? productLabelLastItemsQuantityText : labelLastItemsText}
                      </Label>
                    </View>
                  ) : null}
                </View>
              </View>
            )}

            <Box style={isDesktopScreen ? style.twothird : undefined}>
              <View style={style.productImageWrapper} testID="product-image">
                {product.media ? (
                  <ProductImageGallery
                    handleOnClickImage={onClickImage}
                    handleOnPressButton={onPressProductImageGalleryButton}
                    images={product.media}
                  />
                ) : null}
              </View>

              {isDesktopScreen && (
                <View style={style.distributionsWrapperLarge} onLayout={onLayoutDistributions}>
                  {distributions?.description?.length ? (
                    <Box style={[style.onethird, style.distributionLarge, style.distributionGap]}>
                      <Text level={3} style={style.sectionTitleLarge} variant="action">
                        {productDescriptionTitleText}
                      </Text>

                      <Features features={distributions?.description} />
                    </Box>
                  ) : null}

                  {distributions?.sizeAndFit?.length ? (
                    <Box style={[style.onethird, style.distributionLarge, style.distributionGap]}>
                      <Text level={3} style={style.sectionTitleLarge} variant="action">
                        {productSizeAdjustTitleText}
                      </Text>

                      {product.fashionModel?.size ? (
                        <Text style={style.fashionModelContainer}>
                          <Text level={1} variant="detail">{`${productSizeModelText}: `}</Text>

                          <Text level={1} style={style.fashionModelValue} variant="detail">
                            {product.fashionModel.size}
                          </Text>
                        </Text>
                      ) : null}

                      <Features features={distributions?.sizeAndFit} />
                    </Box>
                  ) : null}

                  {distributions?.composition?.length ? (
                    <Box style={[style.onethird, style.distributionLarge]}>
                      <Text level={3} style={style.sectionTitleLarge} variant="action">
                        {productCompositionCareTitleText}
                      </Text>

                      <Features features={distributions?.composition} />

                      <GarmentCareIcons features={distributions.garmentCare} />
                    </Box>
                  ) : null}
                </View>
              )}
            </Box>

            <Box style={isDesktopScreen ? style.onethird : null}>
              <>
                {!isDesktopScreen ? (
                  <ScrollableColorPicker
                    color={product.color}
                    variantId={variantIdFromRoute}
                    onPressColor={onChangeColor}
                  />
                ) : null}

                <View style={isDesktopScreen ? style.productInfoWrapperLarge : style.productInfoWrapperSmall}>
                  {isDesktopScreen && (
                    <>
                      <View style={style.productBrandPricingLarge}>
                        <View style={style.productBrandLarge}>
                          <Text level={3} style={style.brand} testID="product-brand">
                            {product.brand}
                          </Text>

                          <Text
                            ellipsizeMode={isDesktopScreen ? "tail" : undefined}
                            level={2}
                            style={style.productNameLarge}
                            testID="product-title"
                            variant="action"
                          >
                            {product.title}
                          </Text>
                        </View>

                        {!product.isProductWithDiscount ? (
                          <Text level={2} testID="product-price">
                            {formatCurrency({ amount: financial(product.price.amount) })}
                          </Text>
                        ) : (
                          <View testID="sales-pricing">
                            <View style={style.priceContent}>
                              <Text level={2} style={style.salesPricing} testID="product-price">
                                {formatCurrency({ amount: financial(product.price.amount) })}
                              </Text>

                              <Text level={2} testID="product-discounted-price">
                                {product.price.discountedPrice &&
                                  formatCurrency({ amount: financial(product.price.discountedPrice?.amount) })}
                              </Text>
                            </View>

                            <Text level={1} style={style.discount} testID="product-price-discount" variant="detail">
                              {`-${product.price.discountedPrice?.percentage}%`}
                            </Text>
                          </View>
                        )}

                        <View style={style.labelsContainer}>
                          {!product.hasAvailableSizes ? (
                            <View style={style.labelWrapper} testID="no-stock-view">
                              <Label color={COLOR.BG_WARNING} style={style.noStockLabel}>
                                {labelOutOfStockText}
                              </Label>

                              <ButtonIcon name="info" onPress={showAvailabilityModal} />
                            </View>
                          ) : null}

                          {product.hasAvailableSizes && !product.isAvailable ? (
                            <View style={style.labelWrapper} testID="no-available-label">
                              <Label color={COLOR.BG_WARNING}>{productStockOutModalTitleText}</Label>
                            </View>
                          ) : null}

                          {product.isAvailable && product.lastUnits ? (
                            <View style={style.labelWrapper} testID="last-units">
                              <Label color={COLOR.BG_WARNING_LIGHT}>
                                {product.lastUnitsStock ? productLabelLastItemsQuantityText : labelLastItemsText}
                              </Label>
                            </View>
                          ) : null}
                        </View>
                      </View>

                      <ExpandableColorPicker
                        color={product.color}
                        variantId={variantIdFromRoute}
                        onPressColor={onChangeColor}
                      />
                    </>
                  )}

                  {!isUniqueSize ? (
                    <InputField
                      key={product.variantId}
                      defaultValue={selectedSize}
                      icon="arrow_down"
                      label={prudctSizeSelectorText}
                      name="SizeSelector"
                      options={sizeOptions}
                      style={isDesktopScreen ? style.sizeSelectorLarge : style.sizeSelectorSmall}
                      testID="size-selector"
                      type={INPUT_TYPES.text}
                      value={selectedSize}
                      onChange={(value) => onChangeProductSize(value as string)}
                    />
                  ) : null}

                  {isDesktopScreen ? (
                    <>
                      <View style={style.actionButtonsWrapper}>
                        {!product.isAvailable ? (
                          <Button
                            style={{ button: style.actionButtonLarge }}
                            testID="send-email-stockOut-button"
                            onPress={onPressOpenSoldOutSizeModal}
                          >
                            {productStockOutCtaText}
                          </Button>
                        ) : (
                          <Button
                            disabled={!hasSelectedSize}
                            style={{ button: style.actionButtonLarge }}
                            testID="add-to-cart-button"
                            onPress={onPressAddToBasket}
                          >
                            {productAddToCartCtaText}
                          </Button>
                        )}

                        <TouchableOpacity
                          activeOpacity={ACTIVE_OPACITY}
                          disabled={!hasSelectedSize}
                          style={style.addToWishlistButton}
                          testID="button-wishlist"
                          onPress={onPressWishlist}
                        >
                          <Icon
                            name={product.wishlisted ? "heart_fill" : "heart"}
                            style={{ color: wishlistIconColor }}
                          />
                        </TouchableOpacity>
                      </View>

                      {showBenefits ? (
                        <View style={style.benefitsContainerLarge} onLayout={onLayoutBeneftisBanner}>
                          <StackedBanner
                            imageUrl={benefitsMediaUrl}
                            style={{ wrapper: style.benefitsBannerLarge, title: style.benefitsTitleLarge }}
                            subtitle={benefitsCylSubtitleText}
                            title={benefitsCylTitleText}
                            onImageError={hideBenefits}
                          />
                        </View>
                      ) : null}

                      {showWatchDistributions ? (
                        <Touchable style={style.watchDistributions} onPress={onPressWatchDistributions}>
                          <Icon name="info" style={style.watchDistributionsIcon} />

                          <Text level={1} style={style.watchDistributionsText} variant="detail">
                            {productWatchPpText}
                          </Text>
                        </Touchable>
                      ) : null}
                    </>
                  ) : null}

                  <View>
                    {!isDesktopScreen ? (
                      <>
                        {distributions?.description?.length ? (
                          <Box style={style.distributionSmall}>
                            <Text level={3} style={style.sectionTitleSmall} variant="action">
                              {productDescriptionTitleText}
                            </Text>

                            <Features features={distributions?.description} />
                          </Box>
                        ) : null}

                        {distributions?.sizeAndFit?.length ? (
                          <Box style={style.distributionSmall}>
                            <Text level={3} style={style.sectionTitleSmall} variant="action">
                              {productSizeAdjustTitleText}
                            </Text>

                            {product.fashionModel?.size ? (
                              <Text style={style.fashionModelContainer}>
                                <Text level={isDesktopScreen ? 1 : 2} style={style.fashionModelLabel} variant="detail">
                                  {`${productSizeModelText}: `}
                                </Text>

                                <Text level={isDesktopScreen ? 1 : 2} style={style.fashionModelValue} variant="detail">
                                  {product.fashionModel.size}
                                </Text>
                              </Text>
                            ) : null}

                            <Features features={distributions?.sizeAndFit} />
                          </Box>
                        ) : null}

                        {distributions?.composition?.length ? (
                          <Box style={style.distributionSmall}>
                            <Text level={3} style={style.sectionTitleSmall} variant="action">
                              {productCompositionCareTitleText}
                            </Text>

                            <Features features={distributions?.composition} />

                            <GarmentCareIcons features={distributions.garmentCare} />
                          </Box>
                        ) : null}
                      </>
                    ) : null}

                    <Box style={style.deliveryInfo}>
                      <Text
                        level={3}
                        style={isDesktopScreen ? style.returnInfoTitleLarge : style.returnInfoTitleSmall}
                        variant="action"
                      >
                        {productDeliveryTitleText}
                      </Text>

                      {showDeliveryAndReturn ? (
                        <>
                          {returnAndDeliveryFeatures.map((item) => (
                            <View key={item.label}>
                              <ProductFeature
                                label={formatMessage({ id: item.label })}
                                value={formatMessage({ id: item.value })}
                              />
                            </View>
                          ))}
                        </>
                      ) : (
                        <Text level={isDesktopScreen ? 1 : 2} style={style.sizeGuideText} variant="detail">
                          {productDeliveryBodyText}
                        </Text>
                      )}
                    </Box>
                  </View>
                </View>
              </>
            </Box>
          </View>

          {!isDesktopScreen && showBenefits ? (
            <View style={style.benefitsContainerSmall} onLayout={onLayoutBeneftisBanner}>
              <StackedBanner
                imageUrl={benefitsMediaUrl}
                subtitle={benefitsCylSubtitleText}
                title={benefitsCylTitleText}
                onImageError={hideBenefits}
              />
            </View>
          ) : null}

          {lookIdFromRoute && isEnabledCYLModule ? (
            <OtherLookProducts
              lookId={lookIdFromRoute}
              variantId={variantIdFromRoute}
              onPressSlide={onPressOtherLookProduct}
            />
          ) : null}

          <SimilarProducts variantId={product.variantId} onPressProduct={onPressProduct} />

          <LookAndLikeProducts onPressProduct={onPressProduct} />
        </ScrollView>

        {!isDesktopScreen && !isOpenPreviewModal && (
          <ProductDetailFooter
            isAnySizeSelected={hasSelectedSize}
            isAvailable={Boolean(product.isAvailable)}
            isWishlisted={product.wishlisted}
            onLayout={handleOnLayoutFooter}
            onPressBasket={onPressAddToBasket}
            onPressSoldOut={onPressOpenSoldOutSizeModal}
            onPressWishlist={onPressWishlist}
          />
        )}

        <SoldOutSizeModal
          visible={isOpenSoldOutSizeModal}
          onClose={onCloseSoldOutSizeModal}
          onPressButton={onPressSoldOutModalButton}
        />

        <Modal
          portalHostName="LookIn"
          visible={availabilityModalVisible}
          showCloseButton
          onClose={hideAvailabilityModal}
        >
          <View style={style.modalContent}>
            <Text level={3} style={style.modalTitle} variant="heading">
              {productAvailabilityModalTitleText}
            </Text>

            <Text>{productAvailabilityModalBodyText}</Text>
          </View>
        </Modal>
      </Layout>

      {isOpenPreviewModal && Platform.OS === OS.WEB && product.media && (
        <SliderPreviewModal
          images={product.media}
          isVisible={isOpenPreviewModal}
          selectedIndexImage={selectedIndexImage}
          onClose={onClosePreviewModal}
        />
      )}
    </>
  );
};

export type { ProductDetailProps };
export { ProductDetail };
